import React from 'react';
import { Box, ThemeProvider } from '@mui/material';
import useMarketingFonts from '../marketing/useMarketingFonts';
import useSeoHead from '../marketing/useSeoHead';
import MarketingNav from '../marketing/components/MarketingNav';
import MarketingFooter from '../marketing/components/MarketingFooter';
import { PillarHero, PillarFeatureRow, PillarFeatureGrid, PillarClosingCta } from '../marketing/components/PillarPage';
import { marketingColors as C, marketingFonts as F } from '../theme_landing_page';
import { homepageStyles, homepageTheme } from '../theme_landing_page_2';

const features = [
  { title: 'Rent by flat', body: 'Every payment matched to the right unit — no more portfolio-level guessing.' },
  { title: 'AI invoice capture', body: 'Snap or forward an invoice; MillOS files it to the right property and category.' },
  { title: 'Scoped accountant access', body: 'Free, role-based access with a full audit log so year-end is a non-event.' },
  { title: 'MTD-ready reports', body: 'Quarterly and year-end reports out of the box, ready for Making Tax Digital.' },
  { title: 'Deposit tracking', body: 'Deposit protection references, expiries and follow-ups in one place.' },
  { title: 'Accounting sync', body: 'Two-way sync with Xero, QuickBooks and FreeAgent, plus CSV export.' },
];

const banks: { name: string; label: string; color: string; size: number }[] = [
  { name: 'HSBC', label: 'HSBC', color: '#db0011', size: 13 },
  { name: 'Barclays', label: 'Barclays', color: '#00aeef', size: 10 },
  { name: 'Lloyds Bank', label: 'Lloyds', color: '#006a4d', size: 12 },
  { name: 'NatWest', label: 'NatWest', color: '#5a287d', size: 11 },
  { name: 'Santander UK', label: 'Santander', color: '#ec0000', size: 9.5 },
  { name: 'TSB Bank', label: 'TSB', color: '#00a0d6', size: 14 },
  { name: 'Nationwide Building Society', label: 'Nationwide', color: '#1c0f6b', size: 9 },
  { name: 'Virgin Money', label: 'Virgin', color: '#e10a0a', size: 12 },
  { name: 'First Direct', label: 'first direct', color: '#111111', size: 9 },
  { name: 'Monzo', label: 'monzo', color: '#ff4f40', size: 12 },
  { name: 'Starling Bank', label: 'Starling', color: '#6935d3', size: 11 },
  { name: 'Allica Bank', label: 'Allica', color: '#0a2540', size: 12 },
];

const RentPanel = (
  <>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '12px' }}>
      <Box>
        <Box sx={{ fontFamily: F.display, fontSize: 30, lineHeight: 1, color: C.ink }}>£41,400</Box>
        <Box sx={{ fontSize: 11, color: C.bodySoft }}>rent collected · 98% on time</Box>
      </Box>
      <Box sx={{ fontSize: 11, color: C.success, fontWeight: 600 }}>▲ 12%</Box>
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '5px', height: '44px', marginBottom: '14px' }}>
      {[44, 58, 50, 72, 64, 88, 100].map((h, i) => (
        <Box key={i} sx={{ flex: 1, height: `${h}%`, background: i < 4 ? C.chartBarLight : C.accent, borderRadius: '3px 3px 0 0' }} />
      ))}
    </Box>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: C.panelSoft, border: `1px solid ${C.cardHairline}`, borderRadius: '11px', padding: '11px 13px' }}>
        <Box sx={{ fontSize: 13.5, color: C.ink }}>Flat 1 · 12 Roseberry</Box>
        <Box sx={{ fontSize: 11.5, color: C.success, fontWeight: 600 }}>£1,150 · Paid</Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: C.panelSoft, border: `1px solid ${C.cardHairline}`, borderRadius: '11px', padding: '11px 13px' }}>
        <Box sx={{ fontSize: 13.5, color: C.ink }}>Flat 2 · 12 Roseberry</Box>
        <Box sx={{ fontSize: 11.5, color: C.warning, fontWeight: 600 }}>£1,150 · Due 2d</Box>
      </Box>
    </Box>
  </>
);

const ExpensePanel = (
  <>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
      <Box sx={{ width: 26, height: 26, borderRadius: '7px', background: C.ink, display: 'grid', placeItems: 'center', flex: 'none' }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.white} strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <path d="M14 2v6h6" />
        </svg>
      </Box>
      <Box sx={{ fontSize: 10.5, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: C.ink }}>AI captured · just now</Box>
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', background: C.panelSoft, border: `1px solid ${C.cardHairline}`, borderRadius: '12px', padding: '12px 14px', marginBottom: '12px' }}>
      <Box sx={{ width: 34, height: 34, borderRadius: '8px', background: C.tintSuccess, display: 'grid', placeItems: 'center', color: C.success, fontSize: 15, flex: 'none' }}>🧾</Box>
      <Box sx={{ flex: 1 }}>
        <Box sx={{ fontSize: 13.5, color: C.ink, fontWeight: 600 }}>New fridge · £389</Box>
        <Box sx={{ fontSize: 11, color: C.bodySoft }}>Currys → Flat 2 · Capital allowance</Box>
      </Box>
    </Box>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {[
        { label: 'Repairs & maintenance', value: '£1,240', pct: 64, color: C.accent },
        { label: 'Furnishings (capital)', value: '£820', pct: 42, color: C.violetAccent },
      ].map((row) => (
        <Box key={row.label}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: C.body }}>
            <span>{row.label}</span>
            <Box component="span" sx={{ color: C.ink, fontWeight: 600 }}>{row.value}</Box>
          </Box>
          <Box sx={{ height: 6, borderRadius: '3px', background: C.border, mt: '4px' }}>
            <Box sx={{ width: `${row.pct}%`, height: 6, borderRadius: '3px', background: row.color }} />
          </Box>
        </Box>
      ))}
    </Box>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: `1px solid ${C.tintDivider}`, marginTop: '12px', paddingTop: '10px' }}>
      <Box sx={{ fontSize: 11.5, color: C.bodySoft }}>Logged this quarter</Box>
      <Box sx={{ fontSize: 12.5, color: C.ink, fontWeight: 600 }}>
        £3,480 · <Box component="span" sx={{ color: C.success }}>£696 tax saved</Box>
      </Box>
    </Box>
  </>
);

const AccountantPanel = (
  <>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
        <Box sx={{ width: 32, height: 32, borderRadius: '50%', background: C.avatarBg, display: 'grid', placeItems: 'center', fontSize: 12, color: C.avatarText, fontWeight: 600, flex: 'none' }}>SB</Box>
        <Box>
          <Box sx={{ fontSize: 13, color: C.ink, fontWeight: 600 }}>Sarah Bennett, ACCA</Box>
          <Box sx={{ fontSize: 11, color: C.bodySoft }}>Your accountant</Box>
        </Box>
      </Box>
      <Box sx={{ fontSize: 10.5, color: C.success, fontWeight: 600, background: C.tintSuccess, padding: '3px 9px', borderRadius: '20px' }}>Scoped access</Box>
    </Box>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {[
        { label: 'Q1 income & expenses', action: 'View →' },
        { label: 'SA105 property pages', action: 'Export' },
      ].map((row) => (
        <Box key={row.label} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: C.panelSoft, border: `1px solid ${C.cardHairline}`, borderRadius: '11px', padding: '11px 13px' }}>
          <Box sx={{ fontSize: 13, color: C.ink }}>{row.label}</Box>
          <Box sx={{ fontSize: 11, color: C.accent, fontWeight: 600 }}>{row.action}</Box>
        </Box>
      ))}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: C.panelSoft, border: `1px solid ${C.cardHairline}`, borderRadius: '11px', padding: '11px 13px' }}>
        <Box sx={{ fontSize: 13, color: C.ink }}>Full audit log</Box>
        <Box sx={{ fontSize: 11, color: C.bodySoft }}>142 entries</Box>
      </Box>
    </Box>
    <Box sx={{ fontSize: 11, color: C.bodySoft, marginTop: '12px' }}>Read-only · no access to your bank credentials</Box>
  </>
);

const ExpensesByFlatPanel = (
  <>
    <Box sx={{ border: `1.5px dashed ${C.orbitDashInner}`, borderRadius: '12px', padding: '14px', display: 'flex', alignItems: 'center', gap: '11px', marginBottom: '14px', background: C.uploadBoxBg }}>
      <Box sx={{ width: 32, height: 32, borderRadius: '8px', background: C.accent, display: 'grid', placeItems: 'center', flex: 'none' }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.white} strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 5v14M5 12h14" />
        </svg>
      </Box>
      <Box>
        <Box sx={{ fontSize: 13, color: C.ink, fontWeight: 600 }}>Drop invoice or snap a photo</Box>
        <Box sx={{ fontSize: 11, color: C.bodySoft }}>PDF, JPG · AI reads the amount</Box>
      </Box>
    </Box>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: C.panelSoft, border: `1px solid ${C.cardHairline}`, borderRadius: '11px', padding: '11px 13px' }}>
        <Box sx={{ fontSize: 13, color: C.ink }}>Flat 1</Box>
        <Box sx={{ fontSize: 12, color: C.ink, fontWeight: 600 }}>£640</Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: C.tintBlue, border: `1px solid ${C.highlightBorder}`, borderRadius: '11px', padding: '11px 13px' }}>
        <Box sx={{ fontSize: 13, color: C.ink }}>&nbsp;Flat 2 · new fridge added</Box>
        <Box sx={{ fontSize: 12, color: C.accent, fontWeight: 600 }}>£1,029</Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: C.panelSoft, border: `1px solid ${C.cardHairline}`, borderRadius: '11px', padding: '11px 13px' }}>
        <Box sx={{ fontSize: 13, color: C.ink }}>Flat 3</Box>
        <Box sx={{ fontSize: 12, color: C.ink, fontWeight: 600 }}>£410</Box>
      </Box>
    </Box>
  </>
);

const BanksPanel = (
  <>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Box sx={{ width: 26, height: 26, borderRadius: '7px', background: C.accent, display: 'grid', placeItems: 'center', flex: 'none' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.white} strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 21h18" />
            <path d="M5 21V8l7-4 7 4v13" />
            <path d="M9 21v-6h6v6" />
          </svg>
        </Box>
        <Box sx={{ fontSize: 10.5, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: C.accent }}>Open Banking · 1-click</Box>
      </Box>
      <Box sx={{ fontSize: 10.5, color: C.success, fontWeight: 600, background: C.tintSuccess, padding: '3px 9px', borderRadius: '20px' }}>FCA-regulated</Box>
    </Box>
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '9px' }}>
      {banks.map((bk) => (
        <Box key={bk.name} title={bk.name} sx={{ aspectRatio: '1.5', borderRadius: '11px', background: C.white, border: `1px solid ${C.cardHairline}`, display: 'grid', placeItems: 'center', padding: '6px' }}>
          <Box component="span" sx={{ fontFamily: F.body, fontWeight: 700, fontSize: bk.size, letterSpacing: '-0.02em', color: bk.color, textAlign: 'center', lineHeight: 1.05 }}>
            {bk.label}
          </Box>
        </Box>
      ))}
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '14px', background: C.panelSoft, border: `1px solid ${C.cardHairline}`, borderRadius: '11px', padding: '11px 13px' }}>
      <Box component="span" sx={{ color: C.success, fontSize: 13 }}>✓</Box>
      <Box sx={{ fontSize: 12.5, color: C.ink }}>
        Monzo linked · <Box component="span" sx={{ color: C.bodySoft }}>1,204 transactions read &amp; categorised by AI</Box>
      </Box>
    </Box>
  </>
);

const AccountingCentrePage: React.FC = () => {
  useMarketingFonts();
  useSeoHead({
    title: 'Accounting Centre | Open Banking Rent Tracking & Expense Capture — MillOS',
    description: 'MillOS reconciles rent per flat via Open Banking, captures invoices and receipts with AI, and produces MTD-ready reports — automatically.',
    canonicalPath: '/accounting-centre',
  });

  return (
    <ThemeProvider theme={homepageTheme}>
    <Box className="lv-marketing" sx={{ ...homepageStyles, '--website-eyebrow-scale': 0.85, background: C.white, color: C.ink, fontFamily: F.body, fontSize: 16, lineHeight: 1.5, overflowX: 'clip' }}>
      <MarketingNav />

      <PillarHero
        eyebrow="Agent 02 · Accounting"
        title={<>Rent and expenses, <Box component="em" sx={{ fontStyle: 'italic' }}>reconciled</Box></>}
        body="Read-only Open Banking matches every payment to the right flat, AI files every invoice, and your accountant gets scoped access with a full audit log."
        secondaryLabel="See the software"
        secondaryTo="/pricing"
      />

      <Box sx={{ pt: { xs: '48px', md: '96px' } }} />

      <PillarFeatureRow eyebrow="Read-only Open Banking" title="Rent matched to the right flat, automatically." body="Connect every UK bank in minutes via FCA-regulated, read-only Open Banking. MillOS reads payments — never moves money — and matches each one to the right unit." panel={RentPanel} imageSide="left" tilt="a" />

      <PillarFeatureRow eyebrow="AI expense capture" title="Snap a receipt. It files itself." body="Forward or photograph any invoice and MillOS files it to the right property and category — MTD-ready, and syncable to Xero, QuickBooks and FreeAgent." panel={ExpensePanel} imageSide="right" tilt="b" textFlex="1.1fr" />

      <PillarFeatureRow eyebrow="Accountant access" title="Your accountant sees everything — nothing else." body="Give your accountant free, role-scoped access to view and export income, expenses and SA105-ready reports, with a full audit log. They see the books, never your bank login — so year-end becomes a non-event." panel={AccountantPanel} imageSide="left" tilt="a" textFlex="1.1fr" />

      <PillarFeatureRow eyebrow="Expenses per flat" title="Upload an expense to the exact flat." body="Snap or forward any invoice and assign it to a specific unit in a tap — MillOS splits shared costs, tags the category and keeps a running per-flat total, so every property's profit is always accurate." panel={ExpensesByFlatPanel} imageSide="right" tilt="b" />

      <PillarFeatureRow eyebrow="UK bank integrations" title="Connect any UK bank in one click." body="HSBC, Barclays, Lloyds, NatWest, Santander, TSB, Nationwide, Virgin Money, First Direct, Monzo, Starling, Allica and Metro Bank — link read-only in one click and MillOS's AI reads and categorises every transaction automatically." panel={BanksPanel} imageSide="left" tilt="a" textFlex="1.1fr" />

      <PillarFeatureGrid
        heading="Everything the accounting engine covers"
        iconBg={C.ink}
        icon={
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={C.white} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 21h18" />
            <path d="M5 21V8l7-4 7 4v13" />
            <path d="M9 21v-5h6v5" />
          </svg>
        }
        items={features}
      />

      <PillarClosingCta heading="Make year-end a non-event" />

      <MarketingFooter />
    </Box>
    </ThemeProvider>
  );
};

export default AccountingCentrePage;
